import window from '../injectChromeObject'

export type ExtensionStorageQuotaLevel = 'normal' | 'warning'
export type ExtensionStorageQuotaStatusMetadata = {
  usedPercentage?: number
  remainingBytes?: number
}

export class ExtensionStorageQuotaError extends Error {
  metadata: ExtensionStorageQuotaStatusMetadata

  constructor(metadata: ExtensionStorageQuotaStatusMetadata = {}) {
    super('New value would exceed storage quota')
    this.name = 'StorageQuotaError'
    this.metadata = metadata

    Object.setPrototypeOf(this, ExtensionStorageQuotaError.prototype)
  }
}

export class ExtensionStorageQuotaState {
  message: string
  level: ExtensionStorageQuotaLevel
  metadata: ExtensionStorageQuotaStatusMetadata

  constructor(usedPercentage: number, remainingBytes: number) {
    this.level = this.#getLevel(usedPercentage)
    this.message = this.#getMessage(usedPercentage)
    this.metadata = {
      usedPercentage,
      remainingBytes
    }
  }

  #getMessage(usedPercentage: number): string {
    return `Storage quota is ${(usedPercentage * 100).toFixed(2)}% used`
  }

  #getLevel(usedPercentage: number): ExtensionStorageQuotaLevel {
    return usedPercentage > 0.8 ? 'warning' : 'normal'
  }
}

// https://stackoverflow.com/a/23329386/3151192
function byteLengthCharCode(str: string) {
  // returns the byte length of an utf8 string
  let s = str.length
  for (let i = str.length - 1; i >= 0; i--) {
    const code = str.charCodeAt(i)
    if (code > 0x7f && code <= 0x7ff) s++
    else if (code > 0x7ff && code <= 0xffff) s += 2
    if (code >= 0xdc00 && code <= 0xdfff) i-- //trail surrogate
  }
  return s
}

export const getExtensionStorageQuotaState = async (
  key: string,
  value: string
): Promise<ExtensionStorageQuotaState> => {
  const client = window.chrome.storage.local
  const quota: number = client.QUOTA_BYTES
  const newValueByteSize = byteLengthCharCode(value)

  const [byteInUse, oldValueByteSize] = await Promise.all([
    client.getBytesInUse(),
    client.getBytesInUse(key)
  ])

  const newByteInUse = byteInUse + newValueByteSize - oldValueByteSize
  const usedPercentage = newByteInUse / quota
  const remainingBytes = quota - newByteInUse

  if (usedPercentage > 1.0) {
    throw new ExtensionStorageQuotaError({
      usedPercentage,
      remainingBytes
    })
  }

  return new ExtensionStorageQuotaState(usedPercentage, remainingBytes)
}
